import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { FaTimes } from 'react-icons/fa';
import { IconWrapper } from '../../../components/IconWrapper';
import type { FreeAgentCategory } from '../../../freeagent/api';
import type { BillToCompany } from '../../../services/billToService';
import { BILL_PAYMENT_TERMS_LABELS, CIS_BAND_LABELS, type CisBand, type DirectoryAccount } from '../accounts.model';
import { VAT_TREATMENT_LABELS, formatGbp, type VatTreatment } from '../arrangements.model';
import {
  BILL_LINE_KIND_LABELS,
  CIS_CATEGORY_CODES,
  MAX_BILL_LINES,
  billCategoryChoices,
  billCategoryProblem,
  billDueDate,
  computeBillTotals,
  defaultBillCategory,
  lineNetFrom,
  validateBillInput,
  type AccountBill,
  type AccountBillInput,
  type BillCategoryUrls,
  type BillLine,
  type BillLineKind,
  type BillTotals,
} from '../bills.model';
import { billsService } from '../bills.service';
import { AccountPicker } from './ArrangementForms';

const VAT_TREATMENTS = Object.keys(VAT_TREATMENT_LABELS) as VatTreatment[];
export const BILL_LINE_KINDS = Object.keys(BILL_LINE_KIND_LABELS) as BillLineKind[];
const CIS_BANDS = Object.keys(CIS_BAND_LABELS) as CisBand[];
const CATEGORY_MEMORY_KEY = 'jarvis.bills.freeagentCategories';

type DraftLine = { description: string; kind: BillLineKind; quantity: string; unitPrice: string; net: string };

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function rememberedCategories(): BillCategoryUrls {
  try {
    return JSON.parse(window.localStorage.getItem(CATEGORY_MEMORY_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function rememberCategories(categories: BillCategoryUrls) {
  try {
    window.localStorage.setItem(CATEGORY_MEMORY_KEY, JSON.stringify(categories));
  } catch {
    // Convenience only
  }
}

function draftLine(line: BillLine): DraftLine {
  return {
    description: line.description,
    kind: line.kind,
    quantity: line.quantity != null ? String(line.quantity) : '',
    unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
    net: String(line.net),
  };
}

function parseLine(line: DraftLine): BillLine {
  const parsed: BillLine = { description: line.description.trim(), kind: line.kind, net: Number(line.net) || 0 };
  if (line.quantity.trim() !== '' && Number.isFinite(Number(line.quantity))) parsed.quantity = Number(line.quantity);
  if (line.unitPrice.trim() !== '' && Number.isFinite(Number(line.unitPrice))) parsed.unitPrice = Number(line.unitPrice);
  return parsed;
}

export function TotalsBreakdown({ totals, vatTreatment, legacy }: { totals: BillTotals; vatTreatment: VatTreatment; legacy?: boolean }) {
  const row = (label: string, value: string, strong = false) => (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography level="body-sm">{label}</Typography>
      <Typography level="body-sm" fontWeight={strong ? 'lg' : undefined}>{value}</Typography>
    </Stack>
  );
  return (
    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
      <Stack spacing={0.4}>
        {row('Labour', formatGbp(totals.labourNet))}
        {row('Materials', formatGbp(totals.materialsNet))}
        {totals.otherNet > 0 && row('Other', formatGbp(totals.otherNet))}
        {row('Net', formatGbp(totals.netTotal))}
        {(vatTreatment === 'standard' || totals.vatCharged > 0) && row('VAT charged', formatGbp(totals.vatCharged))}
        {vatTreatment === 'reverse_charge' && row('VAT under reverse charge (we account for it)', formatGbp(totals.reverseChargeVat))}
        {row('Invoice total', formatGbp(totals.grossTotal))}
        {totals.cisRate != null && row(`CIS deduction at ${Math.round(totals.cisRate * 1000) / 10}% of labour`, `− ${formatGbp(totals.cisDeduction)}`)}
        <Divider />
        {row('We pay the subcontractor', formatGbp(totals.payableTotal), true)}
        {legacy && (
          <Typography level="body-xs" textColor="neutral.600">
            As recorded on the old subcontractor invoice{totals.vatCharged > 0 ? ', which charged VAT after deducting CIS' : ''}.
          </Typography>
        )}
      </Stack>
    </Sheet>
  );
}

/** Creates a bill, or edits one when `bill` is passed. */
export function BillFormModal({
  open,
  bill,
  accounts,
  billToCompanies,
  onAccountCreated,
  onClose,
  onSaved,
}: {
  open: boolean;
  bill: AccountBill | null;
  accounts: DirectoryAccount[];
  billToCompanies: BillToCompany[];
  onAccountCreated?: (account: DirectoryAccount) => void;
  onClose: () => void;
  onSaved: (bill: AccountBill) => void;
}) {
  const [accountId, setAccountId] = useState('');
  const [billToCompanyId, setBillToCompanyId] = useState('');
  const [reference, setReference] = useState('');
  const [datedOn, setDatedOn] = useState(today());
  const [dueOn, setDueOn] = useState('');
  // Until the due date is typed, new bills take it from the account's payment terms.
  const [dueTouched, setDueTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [vatTreatment, setVatTreatment] = useState<VatTreatment>('standard');
  const [vatPercent, setVatPercent] = useState('20');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [cisBand, setCisBand] = useState<CisBand>('none');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFreeAgent = !!bill?.freeagentBillUrl;

  useEffect(() => {
    if (!open) return;
    setAccountId(bill?.accountId || '');
    setBillToCompanyId(bill?.billToCompanyId || '');
    setReference(bill?.reference || '');
    setDatedOn(bill?.datedOn || today());
    setDueOn(bill?.dueOn || '');
    setDueTouched(!!bill);
    setDescription(bill?.description || '');
    setVatTreatment(bill?.vatTreatment || 'standard');
    setVatPercent(String(Math.round((bill?.vatPct ?? 0.2) * 1000) / 10));
    setLines(bill
      ? bill.lines.map(draftLine)
      : [{ description: '', kind: 'labour', quantity: '', unitPrice: '', net: '' }]);
    setNotes(bill?.notes || '');
    setCisBand(bill?.cisBand || 'none');
    setFile(null);
    setError(null);
  }, [bill, open]);

  const account = accounts.find((row) => row.id === accountId) || null;
  const paymentTerms = account?.billPaymentTerms || 'none';

  useEffect(() => {
    if (!open || dueTouched) return;
    setDueOn(billDueDate(datedOn, paymentTerms) || '');
  }, [open, dueTouched, datedOn, paymentTerms]);

  const parsedLines = lines.map(parseLine);
  const vatPct = (Number(vatPercent) || 0) / 100;
  const totals = computeBillTotals({ lines: parsedLines, vatTreatment, vatPct, cisBand });

  const updateLine = (index: number, patch: Partial<DraftLine>) => {
    setLines((current) => current.map((line, i) => {
      if (i !== index) return line;
      const next = { ...line, ...patch };
      // Quantity × unit price fills the net; the net can still be typed directly.
      if (('quantity' in patch || 'unitPrice' in patch) && next.quantity.trim() !== '' && next.unitPrice.trim() !== '') {
        next.net = String(lineNetFrom(Number(next.quantity), Number(next.unitPrice)));
      }
      return next;
    }));
  };

  const save = async () => {
    const input: AccountBillInput = {
      accountId,
      reference,
      datedOn,
      dueOn: dueOn || null,
      description,
      lines: parsedLines,
      vatTreatment,
      vatPct,
      cisBand,
      billToCompanyId: billToCompanyId || null,
      notes,
    };
    const problem = validateBillInput(input);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let saved = bill ? await billsService.update(bill.id, input) : await billsService.create(input);
      if (file) saved = await billsService.attach(saved, file);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the bill');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', md: 900 }, maxHeight: '92vh', overflow: 'auto' }}>
        <DialogTitle>{bill ? `Edit bill ${bill.reference}` : 'New bill'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {inFreeAgent && (
              <Alert color="warning" variant="soft">
                This bill is in FreeAgent. After saving, send the change to FreeAgent so the two match.
              </Alert>
            )}
            <Grid container spacing={1.5}>
              <Grid xs={12} md={7}>
                <AccountPicker
                  accounts={accounts}
                  value={accountId}
                  onAccountCreated={onAccountCreated}
                  onChange={(next) => {
                    setAccountId(next);
                    // The account's CIS band is the default for its bills; it can be changed below
                    if (!inFreeAgent) setCisBand(accounts.find((row) => row.id === next)?.cisBand || 'none');
                  }}
                  helper="Who the invoice is from."
                />
              </Grid>
              <Grid xs={12} md={5}>
                <FormControl>
                  <FormLabel>Bill to</FormLabel>
                  <Select
                    value={billToCompanyId || null}
                    placeholder="Choose a Keystone company"
                    onChange={(_, value) => setBillToCompanyId(String(value || ''))}
                  >
                    {billToCompanies.map((company) => <Option key={company.id} value={company.id}>{company.name}</Option>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <FormControl required>
                  <FormLabel>Their invoice number</FormLabel>
                  <Input value={reference} onChange={(event) => setReference(event.target.value)} />
                </FormControl>
              </Grid>
              <Grid xs={6} sm={3} md={2}>
                <FormControl required>
                  <FormLabel>Invoice date</FormLabel>
                  <Input type="date" value={datedOn} onChange={(event) => setDatedOn(event.target.value)} />
                </FormControl>
              </Grid>
              <Grid xs={6} sm={3} md={2}>
                <FormControl>
                  <FormLabel>Due</FormLabel>
                  <Input type="date" value={dueOn} onChange={(event) => { setDueTouched(true); setDueOn(event.target.value); }} />
                  {!dueTouched && paymentTerms !== 'none' ? (
                    <FormHelperText>{BILL_PAYMENT_TERMS_LABELS[paymentTerms]}</FormHelperText>
                  ) : !dueOn && <FormHelperText>Blank: due on receipt.</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid xs={8} sm={4} md={3}>
                <FormControl>
                  <FormLabel>VAT</FormLabel>
                  <Select value={vatTreatment} onChange={(_, value) => value && setVatTreatment(value as VatTreatment)}>
                    {VAT_TREATMENTS.map((treatment) => <Option key={treatment} value={treatment}>{VAT_TREATMENT_LABELS[treatment]}</Option>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={4} sm={2} md={2}>
                <FormControl>
                  <FormLabel>Rate</FormLabel>
                  <Input type="number" value={vatPercent} onChange={(event) => setVatPercent(event.target.value)} endDecorator="%" />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl disabled={inFreeAgent}>
                  <FormLabel>CIS</FormLabel>
                  <Select value={cisBand} onChange={(_, value) => value && setCisBand(value as CisBand)}>
                    {CIS_BANDS.map((band) => <Option key={band} value={band}>{CIS_BAND_LABELS[band]}</Option>)}
                  </Select>
                  <FormHelperText>
                    {inFreeAgent
                      ? 'Fixed by FreeAgent once the bill is created. Delete and re-enter the bill to change it.'
                      : account && account.cisBand !== cisBand
                        ? `Changed from the account default (${CIS_BAND_LABELS[account.cisBand]}).`
                        : 'Their CIS status, from the account. Change it for this bill if needed.'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="e.g. Installs w/e 10 October" />
                </FormControl>
              </Grid>
            </Grid>

            <Stack spacing={0.75}>
              <Typography level="title-sm">Lines (ex VAT)</Typography>
              {lines.map((line, index) => (
                <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Input
                    value={line.description}
                    onChange={(event) => updateLine(index, { description: event.target.value })}
                    placeholder="Description"
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Select value={line.kind} onChange={(_, value) => value && updateLine(index, { kind: value as BillLineKind })} sx={{ width: 130 }}>
                      {BILL_LINE_KINDS.map((kind) => <Option key={kind} value={kind}>{BILL_LINE_KIND_LABELS[kind]}</Option>)}
                    </Select>
                    <Input
                      type="number"
                      value={line.quantity}
                      onChange={(event) => updateLine(index, { quantity: event.target.value })}
                      placeholder="Qty"
                      sx={{ width: 80 }}
                    />
                    <Input
                      type="number"
                      value={line.unitPrice}
                      onChange={(event) => updateLine(index, { unitPrice: event.target.value })}
                      placeholder="Unit"
                      startDecorator="£"
                      sx={{ width: 110 }}
                    />
                    <Input
                      type="number"
                      value={line.net}
                      onChange={(event) => updateLine(index, { net: event.target.value })}
                      startDecorator="£"
                      sx={{ width: 130 }}
                    />
                    <IconButton
                      variant="plain"
                      color="neutral"
                      disabled={lines.length === 1}
                      onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                      aria-label="Remove line"
                    >
                      <IconWrapper icon={FaTimes} />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
              <Stack direction="row" spacing={1}>
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={lines.length >= MAX_BILL_LINES}
                  onClick={() => setLines((current) => [...current, { description: '', kind: 'labour', quantity: '', unitPrice: '', net: '' }])}
                >
                  Add labour
                </Button>
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={lines.length >= MAX_BILL_LINES}
                  onClick={() => setLines((current) => [...current, { description: '', kind: 'materials', quantity: '', unitPrice: '', net: '' }])}
                >
                  Add materials
                </Button>
              </Stack>
            </Stack>

            <TotalsBreakdown totals={totals} vatTreatment={vatTreatment} />
            {cisBand !== 'none' && (
              <Typography level="body-xs" textColor="neutral.600">
                CIS is deducted from labour only, before VAT and excluding materials. FreeAgent works out the recorded deduction from the contact’s band, and JARVIS checks it matches.
              </Typography>
            )}

            <FormControl>
              <FormLabel>Their invoice (PDF or image, up to 5 MB)</FormLabel>
              <Input
                type="file"
                slotProps={{ input: { accept: 'application/pdf,image/png,image/jpeg,image/gif' } }}
                onChange={(event) => setFile((event.target as HTMLInputElement).files?.[0] || null)}
              />
              <FormHelperText>
                {bill?.attachmentName && !file
                  ? `Attached: ${bill.attachmentName}`
                  : 'Optional. Without one, FreeAgent gets the PDF JARVIS writes up from this bill.'}
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea minRows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </FormControl>
            {error && <Alert color="danger">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={save} loading={saving}>{bill ? 'Save' : 'Save bill'}</Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}

/** Sends a bill to FreeAgent, or sends the latest changes to a bill already there. */
export function SendBillModal({
  bill,
  onClose,
  onSent,
}: {
  bill: AccountBill | null;
  onClose: () => void;
  onSent: (bill: AccountBill) => void;
}) {
  const [categories, setCategories] = useState<FreeAgentCategory[]>([]);
  const [chosen, setChosen] = useState<BillCategoryUrls>({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const usedKinds = useMemo(
    () => BILL_LINE_KINDS.filter((kind) => bill?.lines.some((line) => line.kind === kind)),
    [bill]
  );

  useEffect(() => {
    if (!bill) return;
    let alive = true;
    setError(null);
    setLoading(true);
    const remembered = rememberedCategories();
    billsService.freeAgentCategories()
      .then((rows) => {
        if (!alive) return;
        setCategories(rows);
        const pick = (kind: BillLineKind) => {
          const allowed = new Set(billCategoryChoices(kind, bill.cisBand, rows).map((row) => row.url));
          const stored = bill[`${kind}CategoryUrl` as const];
          if (stored && allowed.has(stored)) return stored;
          const fallback = defaultBillCategory(kind, bill.cisBand, rows);
          if (fallback) return fallback;
          return remembered[kind] && allowed.has(remembered[kind] as string) ? remembered[kind] : null;
        };
        setChosen({ labour: pick('labour'), materials: pick('materials'), other: pick('other') });
      })
      .catch((err) => alive && setError(err instanceof Error ? err.message : String(err?.message || 'Could not load FreeAgent categories. Is FreeAgent connected?')))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [bill]);

  if (!bill) return null;
  const updating = !!bill.freeagentBillUrl;
  const problem = loading ? null : billCategoryProblem({ cisBand: bill.cisBand, lines: bill.lines, chosen, categories });
  const noCisCategories = bill.cisBand !== 'none' && !loading && categories.length > 0
    && billCategoryChoices('labour', bill.cisBand, categories).length === 0;

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      rememberCategories(chosen);
      onSent(await billsService.sendToFreeAgent(bill.id, chosen));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the bill');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open onClose={() => !sending && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 580 } }}>
        <DialogTitle>{updating ? `Update bill ${bill.reference} in FreeAgent` : `Send bill ${bill.reference} to FreeAgent`}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography level="body-sm" textColor="neutral.600">
              {updating
                ? 'Replaces the bill’s lines and details in FreeAgent with the ones here.'
                : 'Creates the bill in FreeAgent at its full value, with their invoice (or our PDF of it) attached.'}
              {bill.cisBand !== 'none'
                ? ` The contact is set as a CIS subcontractor at ${CIS_BAND_LABELS[bill.cisBand]} first. Labour goes to ${CIS_CATEGORY_CODES.labour[0]} CIS Labour and materials to ${CIS_CATEGORY_CODES.materials[0]} CIS Materials, so FreeAgent takes the ${formatGbp(bill.totals.cisDeduction)} deduction itself and records the gross cost.`
                : ''}
              {bill.vatTreatment === 'reverse_charge' ? ' The bill is recorded as reverse charge.' : ''}
            </Typography>
            {noCisCategories && (
              <Alert color="warning">
                FreeAgent has no CIS categories (096–099). Turn on CIS for Contractors in FreeAgent, then try again.
              </Alert>
            )}
            {loading ? <CircularProgress size="sm" /> : usedKinds.map((kind) => (
              <FormControl key={kind} required>
                <FormLabel>{BILL_LINE_KIND_LABELS[kind]} category</FormLabel>
                <Select
                  value={chosen[kind] || null}
                  placeholder="Choose a category"
                  onChange={(_, value) => setChosen((current) => ({ ...current, [kind]: value ? String(value) : null }))}
                >
                  {billCategoryChoices(kind, bill.cisBand, categories).map((category) => (
                    <Option key={category.url} value={category.url}>
                      {category.nominal_code} · {category.description}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            ))}
            {problem && !noCisCategories && <FormHelperText>{problem}</FormHelperText>}
            {bill.cisBand !== 'none' && (
              <Alert color="neutral" variant="soft">
                <Typography level="body-xs">
                  When you pay {formatGbp(bill.totals.payableTotal)}, explain the bank transaction in FreeAgent as a <b>Bill Payment</b> against this bill, not as a subcontractor payment. FreeAgent then moves the CIS to 811 for HMRC.
                </Typography>
              </Alert>
            )}
            {error && <Alert color="danger">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={send} loading={sending} disabled={loading || !!problem}>
            {updating ? 'Update FreeAgent' : 'Send to FreeAgent'}
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={sending}>Cancel</Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}

/** Small date prompt for recording payment of a bill kept out of FreeAgent. */
export function MarkPaidModal({
  bill,
  onClose,
  onSaved,
}: {
  bill: AccountBill | null;
  onClose: () => void;
  onSaved: (bill: AccountBill) => void;
}) {
  const [paidOn, setPaidOn] = useState(today());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bill) return;
    setPaidOn(today());
    setError(null);
  }, [bill]);

  if (!bill) return null;
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(await billsService.markPaid(bill, paidOn));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark the bill paid');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 420 } }}>
        <DialogTitle>Mark {bill.reference} as paid</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25}>
            <FormControl>
              <FormLabel>Paid on</FormLabel>
              <Input type="date" value={paidOn} onChange={(event) => setPaidOn(event.target.value)} />
              <FormHelperText>The CIS month on certificates follows the payment date.</FormHelperText>
            </FormControl>
            {error && <Alert color="danger">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={save} loading={saving} disabled={!paidOn}>Mark as paid</Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}

export function useBillToCompanies(): BillToCompany[] {
  const [companies, setCompanies] = useState<BillToCompany[]>([]);
  useEffect(() => {
    let alive = true;
    billsService.billToCompanies().then((rows) => alive && setCompanies(rows)).catch(() => undefined);
    return () => { alive = false; };
  }, []);
  return companies;
}

export function BillTotalsBox({ bill }: { bill: AccountBill }) {
  return (
    <Box sx={{ minWidth: 300 }}>
      <TotalsBreakdown totals={bill.totals} vatTreatment={bill.vatTreatment} legacy={bill.isLegacy} />
    </Box>
  );
}
